// Linked social sign-in accounts on the Account page (GET/POST/DELETE /api/v1/me/social-accounts).
// The list state and the user-facing messages for each failure live ONLY in this module.
import {PROVIDER_LABELS, SOCIAL_PROVIDERS, providerConfigured} from './social';

export const TRY_AGAIN_LATER = 'Try again later.';
export const LOAD_ERROR = 'Could not load your sign-in methods. Try again later.';

const label = (provider) => PROVIDER_LABELS[provider] || 'Social';

/** HTTP status of a failed request, or null when no response came back (network error). */
const statusOf = (err) => (err && err.response && err.response.status) || null;

/**
 * API list -> `[{provider, email}]` in provider order (google, apple, facebook). Unknown providers
 * and duplicates are dropped; a blank email becomes null.
 */
export const sortLinked = (list) => {
  const byProvider = new Map();
  for (const item of Array.isArray(list) ? list : []) {
    if (!item || !SOCIAL_PROVIDERS.includes(item.provider) || byProvider.has(item.provider)) continue;
    const email = typeof item.email === 'string' && item.email ? item.email : null;
    byProvider.set(item.provider, {provider: item.provider, email});
  }
  return SOCIAL_PROVIDERS.filter((p) => byProvider.has(p)).map((p) => byProvider.get(p));
};

/** The list after a successful POST /me/social-accounts ({provider, email}). */
export const withLinked = (list, {provider, email}) =>
  sortLinked([...list.filter((a) => a.provider !== provider), {provider, email}]);

/** The list after a successful DELETE (or a 404: the link is already gone). */
export const withoutLinked = (list, provider) => list.filter((a) => a.provider !== provider);

/**
 * Rows the section shows, in provider order: every linked provider, plus each unlinked provider
 * whose public client id is in the runtime config (an unlinked, unconfigured one is hidden).
 */
export const socialRows = (linked, cfg) => {
  const byProvider = new Map(linked.map((a) => [a.provider, a]));
  return SOCIAL_PROVIDERS.flatMap((provider) => {
    const account = byProvider.get(provider);
    const name = label(provider);
    if (account) return [{provider, label: name, linked: true, email: account.email, canLink: false}];
    if (providerConfigured(provider, cfg)) {
      return [{provider, label: name, linked: false, email: null, canLink: true}];
    }
    return [];
  });
};

/** Failed POST /me/social-accounts -> message. Never says which other account owns the provider. */
export const linkErrorMessage = (provider, err) => {
  const status = statusOf(err);
  const name = label(provider);
  if (status === 409) return 'That account is already linked to a different AIDB account or to this one.';
  if (status === 422) return `Sign-in with ${name} failed. Try again.`;
  if (status === null || status === 502 || status === 503) return TRY_AGAIN_LATER;
  return `Could not link ${name} sign-in. Try again.`;
};

/** Failed DELETE /me/social-accounts/{provider} -> message. */
export const unlinkErrorMessage = (provider, err) => {
  const status = statusOf(err);
  const name = label(provider);
  if (status === 409) return 'Add a password or link another provider before removing this one.';
  if (status === 404) return `${name} sign-in is no longer linked to your account.`;
  if (status === null || status === 502 || status === 503) return TRY_AGAIN_LATER;
  return `Could not remove ${name} sign-in. Try again.`;
};

/** A 404 on DELETE means the link is already gone, so the row should leave the list. */
export const isNotLinked = (err) => statusOf(err) === 404;
